import * as React from "react";
import {
  Button,
  Field,
  Input,
  Spinner,
  Title3,
  Body1,
  Caption1,
  tokens,
} from "@fluentui/react-components";
import { settings, resolveAppToken } from "../services/outlookContext";
import { GlpiClient } from "../services/glpiClient";
import { GlpiError } from "../types/glpi";
import { ErrorBar } from "./ErrorBar";

interface Props {
  onSaved: () => void;
}

/**
 * Écran de premier démarrage / paramétrage : URL GLPI + user_token personnel.
 * Persiste les valeurs dans roamingSettings après un test de connexion réussi.
 */
export function SettingsScreen({ onSaved }: Props): React.ReactElement {
  const envUrl = process.env.GLPI_API_URL || "";
  const roamingUrl = settings.getApiUrl() || "";
  const hasStoredUrl = Boolean(roamingUrl);
  const hasStoredToken = Boolean(settings.getUserToken());

  const [apiUrl, setApiUrl] = React.useState<string>(roamingUrl || envUrl);
  const [userToken, setUserToken] = React.useState<string>("");
  const [testing, setTesting] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const appToken = resolveAppToken();
  const missingAppToken = !appToken;

  async function handleReset(): Promise<void> {
    setResetting(true);
    try {
      await settings.setApiUrl(null);
      await settings.setUserToken(null);
      setApiUrl(envUrl);
      setUserToken("");
      setSuccess(
        "Paramètres locaux effacés. L'URL .env est restaurée, ressaisis ton user-token."
      );
      setError(null);
    } finally {
      setResetting(false);
    }
  }

  async function handleTest(): Promise<void> {
    setError(null);
    setSuccess(null);
    if (!apiUrl) {
      setError("URL de l'API GLPI obligatoire.");
      return;
    }
    if (!userToken) {
      setError("User-token GLPI obligatoire.");
      return;
    }
    if (missingAppToken) {
      setError(
        "App-Token GLPI absent : vérifie .env.development (variable GLPI_APP_TOKEN) puis redémarre `npm start`."
      );
      return;
    }

    setTesting(true);
    const client = new GlpiClient({ apiUrl, appToken, userToken });
    try {
      await client.initSession();
      await client.killSession();
      // Test OK -> on persiste.
      await settings.setApiUrl(apiUrl);
      await settings.setUserToken(userToken);
      setSuccess("Connexion réussie. Paramètres enregistrés.");
      // Petit délai pour que l'utilisateur lise le message avant le switch d'écran.
      window.setTimeout(() => onSaved(), 600);
    } catch (e) {
      if (e instanceof GlpiError) {
        setError(`${e.code} — ${e.message}${friendlyHint(e.code)}`);
      } else {
        const err = e as Error;
        setError(err.message || "Échec de connexion à GLPI.");
      }
    } finally {
      setTesting(false);
    }
  }

  function friendlyHint(code: string): string {
    switch (code) {
      case "ERROR_WRONG_APP_TOKEN_PARAMETER":
        return (
          "\n\nCauses fréquentes :\n" +
          "  • Tu as modifié .env.development après avoir lancé npm start → stoppe puis relance.\n" +
          "  • Le client API correspondant n'existe pas / est inactif dans GLPI (Configuration > Général > API).\n" +
          "  • Restriction d'IP du client API qui ne laisse pas passer l'IP du poste."
        );
      case "ERROR_GLPI_LOGIN_USER_TOKEN":
        return "\n\nLe user-token GLPI est invalide. Le régénérer dans Préférences > Clé d'API distante.";
      case "ERROR_LOGIN_PARAMETERS_MISSING":
        return "\n\nUn des deux tokens (App-Token ou user-token) est vide.";
      case "MIXED_CONTENT":
        return "\n\nMets GLPI en HTTPS ou utilise un proxy HTTPS local.";
      default:
        return "";
    }
  }

  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <Title3 style={{ marginBottom: 0 }}>Configuration GLPI</Title3>
      <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
        Premier démarrage : renseigne l'URL de l'API REST de ton GLPI et ton user-token personnel
        (généré depuis ta fiche utilisateur GLPI &gt; Onglet &laquo;&nbsp;Clé d'API distante&nbsp;&raquo;).
      </Body1>

      <ErrorBar message={error} />
      {success && <ErrorBar message={success} intent="success" />}

      <Field label="URL de l'API GLPI">
        <Input
          value={apiUrl}
          onChange={(_, d) => setApiUrl(d.value)}
          placeholder="https://glpi.jgv.local/apirest.php"
          disabled={testing}
        />
      </Field>

      <Field
        label="User-token GLPI"
        hint="Stocké localement dans Office (roamingSettings). Jamais transmis ailleurs."
      >
        <Input
          type="password"
          value={userToken}
          onChange={(_, d) => setUserToken(d.value)}
          disabled={testing}
        />
      </Field>

      <div
        style={{
          padding: 8,
          border: `1px solid ${tokens.colorNeutralStroke2}`,
          borderRadius: 4,
          backgroundColor: tokens.colorNeutralBackground2,
          fontSize: 11,
          lineHeight: 1.5,
        }}
      >
        <strong>Sources de configuration :</strong>
        <div>
          • URL .env :{" "}
          <code>{envUrl || "(vide)"}</code>
        </div>
        <div>
          • URL mémorisée (roamingSettings) :{" "}
          <code>{roamingUrl || "(non définie)"}</code>
          {hasStoredUrl && (
            <span style={{ color: tokens.colorBrandForeground1 }}> ← utilisée par l'add-in</span>
          )}
          {!hasStoredUrl && envUrl && (
            <span style={{ color: tokens.colorBrandForeground1 }}> .env utilisé par défaut</span>
          )}
        </div>
        <div>
          • App-Token .env :{" "}
          {missingAppToken
            ? "ABSENT (vérifie .env.development puis stoppe/relance npm start)"
            : `${appToken.slice(0, 4)}… (${appToken.length} caractères)`}
        </div>
        <div>
          • User-token mémorisé : {hasStoredToken ? "oui" : "non"}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <Button
          appearance="primary"
          onClick={handleTest}
          disabled={testing || resetting}
          icon={testing ? <Spinner size="tiny" /> : undefined}
          style={{ flex: 1 }}
        >
          {testing ? "Test en cours…" : "Tester et enregistrer"}
        </Button>
        {(hasStoredUrl || hasStoredToken) && (
          <Button
            appearance="secondary"
            onClick={handleReset}
            disabled={testing || resetting}
            icon={resetting ? <Spinner size="tiny" /> : undefined}
            title="Efface l'URL et le user-token mémorisés dans roamingSettings, restaure les valeurs du .env"
          >
            Réinitialiser
          </Button>
        )}
      </div>
    </div>
  );
}
